import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Calendar, 
  DollarSign, 
  ShoppingBag,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Wallet,
  Building2,
  Banknote,
  Clock,
  CalendarCheck,
  UserCheck,
  LogIn,
  LogOut,
  CreditCard,
  Timer,
  AlertTriangle,
  FileText,
  Plane,
  DoorOpen
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { CheckInDialog } from '../components/reservations';
import CheckOutDialog from '../components/CheckOutDialog';
import SimplePunchClock from '../components/SimplePunchClock';
import EmployeePlanning from '../components/EmployeePlanning';
import GuestProfile from '../components/GuestProfile';
import { toast } from 'sonner';
import useConfig from '../hooks/useConfig';
import { GuestLink, ReservationLink, SaleLink } from '../components/crm/EntityLinks';
import { formatCurrency } from '../utils/formatters';
import { OnboardingCTAs } from '../components/onboarding';

// Personal HR Widget for all users with employee_id
const PersonalHRWidget = ({ user }) => {
  const [hrData, setHrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanning, setShowPlanning] = useState(false);

  useEffect(() => {
    if (user?.employee_id) {
      fetchMyHRData();
    } else {
      setLoading(false);
    }
  }, [user?.employee_id]);

  const fetchMyHRData = async () => {
    try {
      const response = await api.get('/employees/me');
      setHrData(response.data);
    } catch (error) {
      console.error('Failed to fetch personal HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!user?.employee_id || !hrData?.linked) return null;

  const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const todayDow = new Date().getDay();

  // Show planning view
  if (showPlanning) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setShowPlanning(false)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <EmployeePlanning employeeId={user.employee_id} isOwner={false} />
      </div>
    );
  }

  return (
    <Card className="border-primary/30 bg-primary/5" data-testid="personal-hr-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserCheck className="w-5 h-5 text-primary" />
          Mon espace RH
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* SimplePunchClock with all 3 buttons */}
        <SimplePunchClock 
          employeeId={user.employee_id} 
          onViewPlanning={() => setShowPlanning(true)}
          compact={true}
        />
        
        {/* Additional Info: Hours and Schedule */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Hours Summary */}
          <div className="p-4 bg-white rounded-xl">
            <p className="text-sm text-muted-foreground mb-2">Mes heures</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Cette semaine</span>
                <span className="text-xl font-bold text-primary">{hrData.hours?.week || 0}h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Ce mois</span>
                <span className="text-xl font-bold text-primary">{hrData.hours?.month || 0}h</span>
              </div>
            </div>
          </div>

          {/* Schedule & Leaves */}
          <div className="p-4 bg-white rounded-xl">
            <p className="text-sm text-muted-foreground mb-2">Mon planning</p>
            <div className="flex gap-1 mb-3">
              {hrData.schedule?.map((day, i) => (
                <div 
                  key={i}
                  className={`flex-1 text-center py-1 rounded text-xs ${
                    day.dayOfWeek === todayDow 
                      ? 'bg-primary text-white' 
                      : day.isWorking 
                        ? 'bg-gray-100' 
                        : 'bg-red-50 text-red-600'
                  }`}
                >
                  {DAYS[day.dayOfWeek]}
                </div>
              ))}
            </div>
            {hrData.leaves?.upcoming?.length > 0 && (
              <p className="text-xs text-blue-600">
                Prochain congé: {format(new Date(hrData.leaves.upcoming[0].startDate), 'dd/MM', { locale: fr })}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Staff Dashboard - simplified with new 3-button punch clock
const StaffDashboard = () => {
  const { user } = useAuth();
  const [hrData, setHrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanning, setShowPlanning] = useState(false);

  useEffect(() => {
    const fetchHRData = async () => {
      try {
        const response = await api.get('/employees/me');
        setHrData(response.data);
      } catch (error) {
        console.error('Failed to fetch HR data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHRData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hrData?.linked) {
    // User is not linked to employee profile - show a simplified dashboard with navigation
    return (
      <div className="space-y-8" data-testid="staff-dashboard">
        <div>
          <h1 className="text-3xl font-semibold">Bonjour, {user?.first_name} !</h1>
          <p className="text-muted-foreground mt-1">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        
        {/* Quick Actions based on permissions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {user?.permissions?.includes('reservations.view') && (
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/reservations'}>
              <CardContent className="py-6 text-center">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold">Réservations</h3>
                <p className="text-sm text-muted-foreground">Gérer les réservations</p>
              </CardContent>
            </Card>
          )}
          {user?.permissions?.includes('calendar.view') && (
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/calendar'}>
              <CardContent className="py-6 text-center">
                <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-blue-600" />
                <h3 className="font-semibold">Calendrier</h3>
                <p className="text-sm text-muted-foreground">Vue calendrier</p>
              </CardContent>
            </Card>
          )}
          {user?.permissions?.includes('guests.view') && (
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/guests'}>
              <CardContent className="py-6 text-center">
                <Users className="w-10 h-10 mx-auto mb-3 text-green-600" />
                <h3 className="font-semibold">Clients</h3>
                <p className="text-sm text-muted-foreground">Gérer les clients</p>
              </CardContent>
            </Card>
          )}
          {user?.permissions?.includes('payments.view') && (
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/encaissements'}>
              <CardContent className="py-6 text-center">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-amber-600" />
                <h3 className="font-semibold">Encaissements</h3>
                <p className="text-sm text-muted-foreground">Voir les paiements</p>
              </CardContent>
            </Card>
          )}
          {user?.permissions?.includes('products.view') && (
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/products'}>
              <CardContent className="py-6 text-center">
                <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-purple-600" />
                <h3 className="font-semibold">Produits</h3>
                <p className="text-sm text-muted-foreground">Catalogue produits</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Info message about HR */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-blue-800 text-sm">
              Pour accéder au pointage et à vos heures de travail, votre compte doit être lié à un profil employé. 
              Contactez votre administrateur si nécessaire.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show planning view (6 months)
  if (showPlanning) {
    return (
      <div className="space-y-4" data-testid="staff-planning-view">
        <Button variant="ghost" onClick={() => setShowPlanning(false)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au pointage
        </Button>
        <EmployeePlanning employeeId={user.employee_id} isOwner={false} />
      </div>
    );
  }

  const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const todayDow = new Date().getDay();

  return (
    <div className="space-y-8" data-testid="staff-dashboard">
      <div>
        <h1 className="text-3xl font-semibold">Bonjour, {user?.first_name} !</h1>
        <p className="text-muted-foreground mt-1">
          {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
        </p>
      </div>

      {/* New Simple Punch Clock with 3 buttons */}
      <SimplePunchClock 
        employeeId={user.employee_id} 
        onViewPlanning={() => setShowPlanning(true)}
      />

      {/* Hours Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-primary" />
              Mes heures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <p className="text-sm text-muted-foreground">Cette semaine</p>
                <p className="text-3xl font-bold text-primary">{hrData.hours?.week || 0}h</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <p className="text-sm text-muted-foreground">Ce mois</p>
                <p className="text-3xl font-bold text-primary">{hrData.hours?.month || 0}h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-purple-600" />
              Mon planning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1">
              {hrData.schedule?.map((day, index) => (
                <div
                  key={index}
                  className={`text-center p-2 rounded-lg ${
                    day.dayOfWeek === todayDow 
                      ? 'bg-primary text-white' 
                      : day.isWorking 
                        ? 'bg-gray-50' 
                        : 'bg-red-50 text-red-600'
                  }`}
                >
                  <p className="font-medium text-xs">{DAYS[day.dayOfWeek].substring(0, 3)}</p>
                  {day.isWorking ? (
                    <p className="text-xs mt-1">{day.startTime?.substring(0,5)}</p>
                  ) : (
                    <p className="text-xs mt-1">Repos</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Leaves */}
      {hrData.leaves?.upcoming?.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Prochains congés</p>
                <p className="text-sm text-blue-700">
                  {hrData.leaves.upcoming.map(l => 
                    `${l.type}: ${format(new Date(l.startDate), 'dd/MM')} - ${format(new Date(l.endDate), 'dd/MM')}`
                  ).join(' • ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Full Dashboard for Owner and Manager
const FullDashboard = ({ showSocieteData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayData, setTodayData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [pendingSales, setPendingSales] = useState([]);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [selectedCheckinReservation, setSelectedCheckinReservation] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [selectedCheckoutReservation, setSelectedCheckoutReservation] = useState(null);
  const [viewingGuest, setViewingGuest] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const currentDate = new Date();
  
  // Load channels and flux types from config
  const { options: configOptions } = useConfig(['CHANNEL', 'FLUX_TYPE']);
  const channels = configOptions.CHANNEL || [];
  const fluxTypes = configOptions.FLUX_TYPE || [];
  
  // Check if user can see financial info
  const canSeeFinancials = user?.permissions?.includes('finance.view') || 
                           user?.role?.toLowerCase() === 'propriétaire' ||
                           user?.role?.toLowerCase() === 'owner';

  // Helper to get channel label and color
  const getChannelInfo = (code) => {
    const channel = channels.find(c => c.code === code);
    return {
      label: channel?.label || code,
      color: channel?.color || '#6B7280'
    };
  };
  
  // Helper to get flux type info
  const getFluxInfo = (code) => {
    const flux = fluxTypes.find(f => f.code === code);
    return {
      label: flux?.label || code,
      color: flux?.color || '#6B7280'
    };
  };
  
  // Group channels by their default_flux
  const channelsByFlux = useMemo(() => {
    const grouped = {};
    channels.forEach(ch => {
      const flux = ch.metadata?.default_flux || 'CASH_CAISSE';
      if (!grouped[flux]) grouped[flux] = [];
      grouped[flux].push(ch);
    });
    return grouped;
  }, [channels]);

  useEffect(() => {
    // Wait for user to be loaded before checking localStorage
    if (!user?.id) return;
    
    // Check if onboarding was dismissed (per user + per riad)
    const dismissKey = `onboarding_dismissed_${user.id}_${user.currentRiad?.id || 'default'}`;
    const wasDismissed = localStorage.getItem(dismissKey) === 'true';
    setOnboardingDismissed(wasDismissed);
    
    const fetchData = async () => {
      try {
        const [todayRes, overviewRes, pendingRes] = await Promise.all([
          api.get('/dashboard/today'),
          api.get('/dashboard', {
            params: {
              year: currentDate.getFullYear(),
              month: currentDate.getMonth() + 1
            }
          }),
          api.get('/sales/pending').catch(() => ({ data: [] }))
        ]);
        setTodayData(todayRes.data);
        setOverviewData(overviewRes.data);
        setPendingSales(pendingRes.data);
        
        // Set onboarding stats for new riads
        const stats = overviewRes.data?.stats || {};
        const statsData = {
          reservations: stats.total_reservations || 0,
          guests: stats.total_guests || 0,
          expenses: stats.total_expenses || 0
        };
        setOnboardingStats(statsData);
        
        // Show onboarding only if not dismissed AND not all tasks completed
        const allCompleted = statsData.reservations > 0 && statsData.guests > 0 && statsData.expenses > 0;
        setShowOnboarding(!wasDismissed && !allCompleted);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, user?.currentRiad?.id]);

  // Handle onboarding dismissal - persist to localStorage
  const handleDismissOnboarding = () => {
    if (!user?.id) return;
    const dismissKey = `onboarding_dismissed_${user.id}_${user.currentRiad?.id || 'default'}`;
    localStorage.setItem(dismissKey, 'true');
    setShowOnboarding(false);
    setOnboardingDismissed(true);
  };

  // Handle checkout - open dialog
  const handleCheckout = (reservation) => {
    setSelectedCheckoutReservation(reservation);
    setShowCheckoutDialog(true);
  };

  // Navigate to reservation
  const goToReservation = (reservationId) => {
    navigate(`/reservations?id=${reservationId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If viewing guest profile
  if (viewingGuest) {
    return (
      <GuestProfile 
        guest={viewingGuest}
        onBack={() => setViewingGuest(null)}
        onGuestUpdate={(updated) => setViewingGuest(updated)}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">
            {format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm px-3 py-1">
            <Calendar className="w-4 h-4 mr-2" />
            {format(currentDate, "MMMM yyyy", { locale: fr })}
          </Badge>
        </div>
      </div>

      {/* Onboarding CTAs for new riads */}
      {showOnboarding && onboardingStats && (
        <OnboardingCTAs 
          stats={onboardingStats} 
          onDismiss={handleDismissOnboarding} 
        />
      )}

      {/* Today Section */}
      <section>
        <h2 className="text-xl font-medium mb-4">Aujourd'hui</h2>
        
        {/* Pending Sales Alert - Only for users who can see financials */}
        {canSeeFinancials && pendingSales.length > 0 && (
          <Card className="border-orange-200 bg-orange-50 mb-4" data-testid="pending-sales-alert">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <p className="font-medium text-orange-800">
                    {pendingSales.length} ticket(s) en attente de paiement
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/sales')}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Voir les tickets
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {pendingSales.slice(0, 5).map((sale) => (
                  <SaleLink 
                    key={sale.id} 
                    sale={sale} 
                    variant="badge"
                    className="bg-white text-orange-700 border border-orange-200 hover:bg-orange-50"
                  />
                ))}
                {pendingSales.length > 5 && (
                  <Badge className="bg-orange-200 text-orange-800">
                    +{pendingSales.length - 5} autres
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-in Alert */}
        {todayData?.arrivals?.filter(a => a.status === 'CONFIRMED').length > 0 && (
          <Card className="border-primary bg-primary/5 animate-pulse-slow mb-4" data-testid="checkin-alert">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-full">
                    <Plane className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary">
                      Check-in requis !
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {todayData.arrivals.filter(a => a.status === 'CONFIRMED').length} arrivée(s) aujourd'hui
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {todayData.arrivals.filter(a => a.status === 'CONFIRMED').slice(0, 2).map((arrival) => (
                    <Button
                      key={arrival.id}
                      onClick={() => {
                        setSelectedCheckinReservation(arrival);
                        setShowCheckinDialog(true);
                      }}
                      className="bg-primary hover:bg-primary/90"
                      data-testid={`checkin-btn-${arrival.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      {arrival.guest?.first_name} {arrival.guest?.last_name}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-primary/20">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Collectez les passeports, la taxe de séjour et imprimez la fiche de police
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-out Alert - Departures today */}
        {todayData?.departures?.length > 0 && (
          <Card className="border-orange-300 bg-orange-50 mb-4" data-testid="checkout-alert">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-200 rounded-full shrink-0">
                    <DoorOpen className="w-6 h-6 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-orange-800">
                      Check-out à effectuer !
                    </p>
                    <p className="text-sm text-orange-600">
                      {todayData.departures.length} départ(s) prévu(s) aujourd'hui
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  {todayData.departures.slice(0, 2).map((departure) => (
                    <div key={departure.id} className="flex items-center gap-2 bg-white/50 p-2 rounded-lg">
                      <Button
                        onClick={() => handleCheckout(departure)}
                        variant="outline"
                        size="sm"
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                        data-testid={`checkout-btn-${departure.id}`}
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Check-out
                      </Button>
                      <GuestLink 
                        guest={departure.guest} 
                        className="text-orange-700 text-sm truncate max-w-[120px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-sm text-orange-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Vérifiez que tous les tickets sont payés avant de valider le départ
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Balance Reminder Alert for Direct Reservations - Only for users who can see financials */}
        {canSeeFinancials && todayData?.balanceAlerts?.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50 mb-4" data-testid="balance-alerts">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-200 rounded-full">
                    <Wallet className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800">
                      💰 Solde à demander
                    </p>
                    <p className="text-sm text-emerald-600">
                      {todayData.balanceAlerts.length} réservation(s) directe(s) en attente de paiement
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/calendar')}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                >
                  Voir les réservations
                </Button>
              </div>
              <div className="mt-3 grid gap-2">
                {todayData.balanceAlerts.slice(0, 3).map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between bg-white p-2 rounded border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <GuestLink guest={alert.guest} />
                      <span className="text-sm text-muted-foreground">
                        Arrivée: {format(new Date(alert.check_in), 'dd/MM')}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-emerald-700">
                        {formatCurrency(parseFloat(alert.total_amount || 0) - parseFloat(alert.deposit_amount || 0))}
                      </span>
                      <span className="text-xs text-muted-foreground block">à percevoir</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Current Guest */}
          <Card className="card-metric" data-testid="current-guest-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Client en séjour
              </CardTitle>
              <Users className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              {todayData?.current ? (
                <div 
                  className="cursor-pointer hover:bg-accent/50 -mx-4 px-4 py-2 rounded-lg transition-colors"
                  onClick={() => goToReservation(todayData.current.id)}
                >
                  <p className="text-xl font-semibold">{todayData.current.guest}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={`badge-${todayData.current.source.toLowerCase()}`}>
                      {todayData.current.source}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {todayData.current.pax} pax
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Aucun client</p>
              )}
            </CardContent>
          </Card>

          {/* Arrivals */}
          <Card className="card-metric" data-testid="arrivals-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Arrivées
              </CardTitle>
              <ArrowDownRight className="w-5 h-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-semibold">{todayData?.arrivals?.length || 0}</p>
              {todayData?.arrivals?.length > 0 && (
                <div className="mt-1">
                  <GuestLink 
                    guest={todayData.arrivals[0].guest} 
                    className="text-sm"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Departures */}
          <Card className="card-metric" data-testid="departures-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Départs
              </CardTitle>
              <ArrowUpRight className="w-5 h-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-semibold">{todayData?.departures?.length || 0}</p>
              {todayData?.departures?.length > 0 && (
                <div className="mt-1">
                  <GuestLink 
                    guest={todayData.departures[0].guest} 
                    className="text-sm"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cash Balance - Only for users who can see financials */}
          {canSeeFinancials && (
            <Card className="card-metric" data-testid="cash-balance-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Fond de caisse
                </CardTitle>
                <Wallet className="w-5 h-5 text-amber-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold text-amber-600">
                  {formatCurrency(todayData?.cashBalance || 0)}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="text-green-600">
                    +{formatCurrency(todayData?.sales?.total || 0)}
                  </span>
                  <span className="text-red-600">
                    -{formatCurrency(todayData?.expenses?.totalCaisse || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Monthly Overview */}
      <section>
        <h2 className="text-lg sm:text-xl font-medium mb-4">Vue d'ensemble du mois</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Occupancy */}
          <Card className="card-metric" data-testid="occupancy-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Taux d'occupation
              </CardTitle>
              <Calendar className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold text-primary">
                {overviewData?.occupancy?.rate || 0}%
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {overviewData?.occupancy?.nights || 0} / {overviewData?.occupancy?.totalNights || 30} nuits
              </p>
            </CardContent>
          </Card>

          {/* Revenue - Only for owner */}
          {showSocieteData && overviewData?.revenue && (
            <Card className="card-metric" data-testid="revenue-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Revenus réservations
                </CardTitle>
                <DollarSign className="w-5 h-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold text-green-600">
                  {formatCurrency(overviewData?.revenue?.total || 0)}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {overviewData?.revenue?.byChannel && Object.entries(overviewData.revenue.byChannel).map(([code, amount]) => {
                    const channelInfo = getChannelInfo(code);
                    return (
                      <Badge 
                        key={code}
                        style={{ 
                          backgroundColor: `${channelInfo.color}20`,
                          color: channelInfo.color,
                          borderColor: `${channelInfo.color}40`
                        }}
                        className="text-xs border"
                      >
                        {channelInfo.label}: {formatCurrency(amount)}
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales - Only for users who can see financials */}
          {canSeeFinancials && (
            <Card className="card-metric" data-testid="sales-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Ventes extras
                </CardTitle>
                <ShoppingBag className="w-5 h-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold text-purple-600">
                  {formatCurrency(overviewData?.sales?.total || 0)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {overviewData?.sales?.count || 0} ventes
                </p>
              </CardContent>
            </Card>
          )}

          {/* Expenses - Only for users who can see financials */}
          {canSeeFinancials && (
            <Card className="card-metric" data-testid="expenses-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Dépenses {showSocieteData ? 'totales' : 'caisse'}
                </CardTitle>
                <TrendingDown className="w-5 h-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold text-red-600">
                  {formatCurrency(showSocieteData ? (overviewData?.expenses?.total || 0) : (overviewData?.expenses?.caisse || 0))}
                </p>
                {showSocieteData && (
                  <div className="flex gap-2 mt-2 text-xs">
                    <Badge className="badge-societe">
                      Société: {formatCurrency(overviewData?.expenses?.societe || 0)}
                    </Badge>
                    <Badge className="badge-caisse">
                      Caisse: {formatCurrency(overviewData?.expenses?.caisse || 0)}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Financial Summary - Only for owner - SIMPLIFIED AND CLEANER */}
      {showSocieteData && overviewData?.revenue && (
        <section>
          <h2 className="text-xl font-medium mb-4">Synthèse financière</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Card 1: Revenus du mois */}
            <Card className="card-metric bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200/50" data-testid="revenue-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Revenus du mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-700">
                  {formatCurrency(overviewData?.revenue?.total || 0)}
                </div>
                {overviewData?.revenue?.commissions > 0 && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Dont commissions: -{formatCurrency(overviewData?.revenue?.commissions || 0)}
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-emerald-200/50">
                  <p className="text-xs text-emerald-600">
                    Net après commissions: <span className="font-semibold">{formatCurrency((overviewData?.revenue?.total || 0) - (overviewData?.revenue?.commissions || 0))}</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Solde caisse */}
            <Card className="card-metric bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50" data-testid="cash-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  Fond de caisse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-700">
                  {formatCurrency(overviewData?.cash?.balance || 0)}
                </div>
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-amber-600">Entrées</span>
                    <span className="text-green-600 font-medium">+{formatCurrency(overviewData?.cash?.in || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-amber-600">Sorties</span>
                    <span className="text-red-600 font-medium">-{formatCurrency(overviewData?.cash?.out || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Dépenses */}
            <Card className="card-metric bg-gradient-to-br from-red-50 to-red-100/50 border-red-200/50" data-testid="expenses-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Dépenses du mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-700">
                  {formatCurrency((overviewData?.expenses?.caisse || 0) + (overviewData?.expenses?.societe || 0))}
                </div>
                <div className="mt-3 space-y-1">
                  {overviewData?.expenses?.caisse > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">Caisse</span>
                      <span className="text-red-700 font-medium">{formatCurrency(overviewData?.expenses?.caisse || 0)}</span>
                    </div>
                  )}
                  {overviewData?.expenses?.societe > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">Charges</span>
                      <span className="text-red-700 font-medium">{formatCurrency(overviewData?.expenses?.societe || 0)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown by channel - only if multiple channels have data */}
          {Object.keys(overviewData?.revenue?.byChannel || {}).filter(ch => overviewData?.revenue?.byChannel[ch] > 0).length > 1 && (
            <Card className="mt-4 border-stone-200/50" data-testid="revenue-by-channel-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-stone-700">Répartition par source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(overviewData?.revenue?.byChannel || {})
                    .filter(([, amount]) => amount > 0)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 4)
                    .map(([channelCode, amount]) => {
                      const channel = configOptions.CHANNEL?.find(ch => ch.code === channelCode);
                      return (
                        <div key={channelCode} className="text-center p-3 rounded-lg bg-stone-50">
                          <p className="text-xs text-muted-foreground mb-1">{channel?.label || channelCode}</p>
                          <p className="font-semibold text-stone-700">{formatCurrency(amount)}</p>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* Alerts */}
      {overviewData?.alerts?.lowStock > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <p className="text-orange-800">
              <span className="font-semibold">{overviewData.alerts.lowStock}</span> produit(s) en stock faible
            </p>
          </CardContent>
        </Card>
      )}

      {showSocieteData && overviewData?.pending?.payouts > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="flex items-center gap-3 py-4">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <p className="text-blue-800">
              <span className="font-semibold">{formatCurrency(overviewData.pending.payouts)}</span> en attente de versement des plateformes
            </p>
          </CardContent>
        </Card>
      )}

      {/* Personal HR Widget for all users with employee_id */}
      <PersonalHRWidget user={user} />

      {/* Check-in Dialog */}
      <CheckInDialog
        reservation={selectedCheckinReservation}
        open={showCheckinDialog}
        onOpenChange={setShowCheckinDialog}
        onComplete={() => {
          setShowCheckinDialog(false);
          fetchTodayData();
          fetchDashboardMetrics();
        }}
        onRefresh={() => {
          // Just refresh data, don't navigate away
          fetchTodayData();
        }}
      />

      {/* Check-out Dialog */}
      <CheckOutDialog
        reservation={selectedCheckoutReservation}
        open={showCheckoutDialog}
        onOpenChange={setShowCheckoutDialog}
        onSuccess={() => {
          // Refresh dashboard data
          window.location.reload();
        }}
      />
    </div>
  );
};

export default function DashboardPage() {
  const { can, user } = useAuth();
  
  // Staff only sees HR dashboard
  if (can('dashboard.staff') && !can('dashboard.manager') && !can('dashboard.owner')) {
    return <StaffDashboard />;
  }
  
  // Owner sees everything, Manager sees without SOCIETE data
  const showSocieteData = can('societe.view');
  
  return <FullDashboard showSocieteData={showSocieteData} />;
}
